<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch } from 'vue';
import Toast from './components/Toast.vue';
import { Analytics } from './analytics';
import { useRaffleApi } from './services';
import { useSEO } from './utils/useSEO';
import { useSchema } from './utils/useSchema';
import { useOpenGraph } from './utils/useOpenGraph';

// Component Imports
import NavBar from './components/NavBar.vue';
import Footer from './components/Footer.vue';
import CheckoutModal from './components/CheckoutModal.vue';
import SuccessModal from './components/SuccessModal.vue';
import MyTicketsModal from './components/MyTicketsModal.vue';
import TicketVerifier from './components/TicketVerifier.vue';
import StaticContent from './components/StaticContent.vue';
import PrizeShowcase from './components/PrizeShowcase.vue';
import TicketGrid from './components/TicketGrid.vue';
import ElLazoGanaderoLanding from './components/ElLazoGanaderoLanding.vue';
import FeedbackModal from './components/FeedbackModal.vue';
import { usePusher } from './composables/usePusher';

// Initialize SEO & Schema composables
const { setPageSEO, updateRaffleSEO } = useSEO();
const { initBaseSchemas, addProductSchema, addEventSchema, addBreadcrumbSchema } = useSchema();
const { updateSocialMedia, updateRaffleSocial } = useOpenGraph();

const api = useRaffleApi();
const loading = ref(true);
const raffles = ref<any[]>([]);
const currentRaffle = ref<any>(null);
const closedRaffles = ref<any[]>([]); 

// Pusher Real-time
const { isConnected: pusherConnected, latestTickets } = usePusher('raffle-default');
// 1: New State for Quantity Selection
const selectedQuantity = ref(0); // 1, 3, or 10

// 1.1: Manual Selection State
const selectedTicketNumbers = ref<number[]>([]);
const selectionMode = ref<'random' | 'manual'>('random');
const manualSearchNumber = ref<number | null>(null);

// 1.5 Exchange Rate State
const exchangeRate = ref(525); // Default Fallback - Tasa del día
const exchangeDate = ref(new Date().toISOString().split('T')[0]);

// Global App State (Missing Definitions)
const flowStep = ref<'landing' | 'register' | 'select_tickets' | 'verify' | 'winners' | 'terms' | 'privacy' | 'rules' | 'help' | 'contact' | 'how_it_works' | 'my_tickets'>('landing');
const showLazoLanding = ref(true);
const purchaseState = ref<'idle' | 'reserving' | 'success'>('idle');
const userPhone = ref('');
const userEmail = ref('');
const showCheckout = ref(false);
const takenTickets = ref<number[]>([]);
const soldTickets = ref<number[]>([]);
const reservedTickets = ref<number[]>([]);

const feedback = ref<{ show: boolean; title: string; message: string; type: 'success' | 'error' | 'info' | 'warning' }>({
  show: false,
  title: '',
  message: '',
  type: 'info',
});


// 2: Pricing Logic with PROMOS (Dynamic)
const totalAmount = computed(() => {
  if (!currentRaffle.value) return 0;

  let qty =
    selectionMode.value === 'manual' ? selectedTicketNumbers.value.length : selectedQuantity.value;
  let price = 0;

  // 1. Bundles from DB
  const dbBundles = currentRaffle.value.ticketBundles || [];
  // Sort desc by qty
  const sortedBundles = [...dbBundles].sort((a: any, b: any) => b.qty - a.qty);

  for (const bundle of sortedBundles) {
    if (qty >= bundle.qty) {
      const numBundles = Math.floor(qty / bundle.qty);
      price += numBundles * bundle.price;
      qty = qty % bundle.qty;
    }
  }

  // 2. Remainder at Unit Price
  const useUnitPrice = currentRaffle.value.ticketPrice || 20;
  price += qty * useUnitPrice;

  return price;
});

const verifyParams = ref<{ id: string; sig: string } | null>(null);

// Toast State
const toast = ref<{ show: boolean; message: string; type: 'success' | 'error' | 'info' }>({
  show: false,
  message: '',
  type: 'info',
});

const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
  toast.value = { show: true, message, type };
  setTimeout(() => {
    toast.value.show = false;
  }, 5000); // Auto close
};

// Offline State
const isOffline = ref(!navigator.onLine);

// Load Data
onMounted(async () => {
  // Offline Listeners
  window.addEventListener('offline', () => {
    isOffline.value = true;
    showToast('Sin conexión a internet', 'error');
  });
  window.addEventListener('online', () => {
    isOffline.value = false;
    showToast('Conexión restablecida', 'success');
  });

  // Campaign Route Check - Set flag but don't return early
  // We still need to load raffle data for hero section
  if (
    window.location.pathname.includes('/el-lazo-ganadero') ||
    window.location.hash === '#lazo-ganadero' ||
    window.location.pathname === '/'
  ) {
    showLazoLanding.value = true;
    // Don't return early - continue loading raffle data
  }

  // Initialize Base Schemas & SEO
  initBaseSchemas();
  setPageSEO('landing');
  updateSocialMedia({
    title: 'Rifas La Favorita - ¡Atrapa tu Suerte!',
    description:
      'Participa en los mejores sorteos y gana premios increíbles con Rifas La Favorita.',
    ogImage: 'https://media.lafavoritarifas.com/web/og-image.png',
  });

  // Sentry Verification Helper
  (window as any).verifySentry = () => {
    console.log('Testing Sentry...');
    // @ts-ignore
    myUndefinedFunction();
  };

  // Check for Verification URL
  const urlParams = new URLSearchParams(window.location.search);
  const verifyId = urlParams.get('verify');
  const verifySig = urlParams.get('sig');

  if (verifyId && verifySig) {
    verifyParams.value = { id: verifyId, sig: verifySig };
    flowStep.value = 'verify';
    loading.value = false;
    return;
  }

  try {
    const [res, rateRes, closedRes] = await Promise.all([
      api.getSorteos(),
      api.getConfig().catch(() => ({ exchange_rate: 525, server_time: new Date().toISOString() })),
      api.getSorteosCerrados().catch(() => ({ data: [] })),
    ]);

    // Update Rate
    exchangeRate.value = rateRes.exchange_rate || 525;
    exchangeDate.value = rateRes.server_time;

    raffles.value = res.data;
    closedRaffles.value = closedRes.data || [];

    if (raffles.value.length > 0) {
      // Prioritize 150 Mautes Raffle
      const lazoRaffle = raffles.value.find((r: any) => r.id === 'R_GRAN_RIFA_150_MAUTES');
      
      if (lazoRaffle) {
        currentRaffle.value = lazoRaffle;
        showLazoLanding.value = true;
      } else {
        currentRaffle.value = raffles.value[0];
      }

      updateRaffleSEO(currentRaffle.value);
      updateRaffleSocial(currentRaffle.value);
      addProductSchema(currentRaffle.value, 4);
      addEventSchema(currentRaffle.value);
      await loadTickets();
    }
  } catch (e) {
    console.error(e);
  } finally {
    loading.value = false;
    // SEO & Analytics Init
    Analytics.initWebVitals();
    Analytics.initCampaignTracking();
    Analytics.pageView('/', 'Inicio - Rifas La Favorita');

    if (currentRaffle.value) {
      Analytics.viewItem(currentRaffle.value);
    }
  }
});

onUnmounted(() => {
  window.removeEventListener('offline', () => {});
  window.removeEventListener('online', () => {});
});

// Watch for Pusher updates
watch(latestTickets, (newTickets) => {
  if (newTickets && currentRaffle.value && newTickets.raffleId === currentRaffle.value.id) {
    console.log('[App] Tickets sold via Pusher:', newTickets.ticketNumbers);
    loadTickets(); // Refresh taken tickets
  }
});

const loadTickets = async () => {
  if (!currentRaffle.value) return;
  const res = await api.getBoletos(currentRaffle.value.id);
  takenTickets.value = res.taken; // array of numbers
  soldTickets.value = res.sold || [];
  reservedTickets.value = res.reserved || [];
};

const toggleTicket = (ticketNumber: number) => {
  if (takenTickets.value.includes(ticketNumber)) return;

  const index = selectedTicketNumbers.value.indexOf(ticketNumber);
  if (index > -1) {
    selectedTicketNumbers.value.splice(index, 1);
  } else {
    if (selectedTicketNumbers.value.length >= 100) {
      showToast('Máximo 100 tickets por compra', 'error');
      return;
    }
    selectedTicketNumbers.value.push(ticketNumber);
  }
};

const jumpToTicket = (ticketNumber: number) => {
  if (ticketNumber < 0 || ticketNumber > (currentRaffle.value?.total_tickets || 9999)) return;

  if (takenTickets.value.includes(ticketNumber)) {
    showToast(`El ticket #${ticketNumber.toString().padStart(4, '0')} no está disponible`, 'error');
    return;
  }

  const button = document.querySelector(`[data-ticket-number="${ticketNumber}"]`);
  if (button) {
    button.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Highlight effect
    button.classList.add('ring-4', 'ring-white');
    setTimeout(() => button.classList.remove('ring-4', 'ring-white'), 1000);
  }
};

const handleLazoBuy = (data: { raffle: any; quantity: number }) => {
  // Override current raffle with the specific Lazo data
  currentRaffle.value = data.raffle;
  selectedQuantity.value = data.quantity;

  // Start purchase flow
  Analytics.push('begin_purchase_flow', { source: 'lazo_landing', raffle: data.raffle.title });
  flowStep.value = 'register';
  showLazoLanding.value = false; // Hide landing to show register steps
};

const startPurchase = () => {
  flowStep.value = 'register';
  Analytics.push('begin_purchase_flow', { source: 'hero_cta', raffle: currentRaffle.value?.title });
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleRegister = async () => {
  if (!userPhone.value || userPhone.value.length < 10) return;
  flowStep.value = 'select_tickets';
  Analytics.push('phone_registered', { phone: userPhone.value });
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const openCheckout = async () => {
  if (
    (selectionMode.value === 'random' && selectedQuantity.value === 0) ||
    (selectionMode.value === 'manual' && selectedTicketNumbers.value.length === 0)
  )
    return;

  if (currentRaffle.value) {
    Analytics.beginCheckout(
      currentRaffle.value,
      selectionMode.value === 'manual'
        ? selectedTicketNumbers.value.length
        : selectedQuantity.value,
      totalAmount.value,
    );
  }

  // Refresh Rate before showing price
  try {
    const config = await api.getConfig();
    if (config.exchange_rate) {
      exchangeRate.value = config.exchange_rate;
      exchangeDate.value = config.server_time || new Date().toISOString();
    }
  } catch (e) {
    console.warn('Could not refresh rate', e);
  }

  showCheckout.value = true;
};

// State
const showSuccess = ref(false);
const showMyTickets = ref(false);
const lastOrderData = ref<any>(null); // Store order details for ticket

const handleSuccessClose = () => {
  showSuccess.value = false;
  flowStep.value = 'landing';
  userPhone.value = '';
  lastOrderData.value = null;
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleNavigate = (step: string) => {
  if (step === 'my_tickets') {
    showMyTickets.value = true;
    setPageSEO('my_tickets');
  } else if (step === 'active_raffles') {
    flowStep.value = 'landing';
    setPageSEO('landing');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  } else {
    // View Transitions API
    if (document.startViewTransition) {
      document.startViewTransition(() => {
        flowStep.value = step as any;
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    } else {
      flowStep.value = step as any;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    // Update SEO based on page
    const pageConfig: Record<string, { title: string; description: string }> = {
      how_it_works: {
        title: 'Cómo Participar - Rifas La Favorita',
        description:
          'Aprende cómo participar en nuestros sorteos. Elige tus tickets, realiza el pago y recibe tu certificado.',
      },
      contact: {
        title: 'Contáctanos - Rifas La Favorita',
        description:
          '¿Tienes dudas? Contáctanos. Atención al cliente, soporte de pagos y ventas disponibles.',
      },
      winners: {
        title: 'Ganadores Anteriores - Rifas La Favorita',
        description:
          'Conoce a los ganadores de nuestros sorteos anteriores. Transparencia total en todos nuestros resultados.',
      },
      terms: {
        title: 'Términos y Condiciones - Rifas La Favorita',
        description:
          'Términos y condiciones oficiales de Rifas La Favorita. Reglas, políticas de devolución y derechos de los participantes.',
      },
      privacy: {
        title: 'Política de Privacidad - Rifas La Favorita',
        description:
          'Política de privacidad de Rifas La Favorita. Cómo protegemos tus datos personales y transacciones.',
      },
      help: {
        title: 'Ayuda - Rifas La Favorita',
        description: 'Preguntas frecuentes y ayuda para participar en nuestros sorteos.',
      },
    };

    const config = pageConfig[step];
    if (config) {
      setPageSEO(step);
      updateSocialMedia({
        title: config.title,
        description: config.description,
        ogUrl: `https://www.lafavoritarifas.com/#${step}`,
      });
    }

    Analytics.pageView(`/${step}`, step);

    // Add breadcrumb schema for better navigation
    addBreadcrumbSchema([
      { name: 'Inicio', item: 'https://www.lafavoritarifas.com/' },
      {
        name: step.charAt(0).toUpperCase() + step.slice(1),
        item: `https://www.lafavoritarifas.com/#${step}`,
      },
    ]);
  }
};

const handlePurchase = async (clientData: any) => {
  purchaseState.value = 'reserving';
  try {
    // Check local availability estimate (optional but good UX)
    const ticketsToCheck =
      selectionMode.value === 'manual'
        ? selectedTicketNumbers.value.length
        : selectedQuantity.value;
    if ((currentRaffle.value.total_tickets || 1000) - takenTickets.value.length < ticketsToCheck) {
      throw new Error('No hay suficientes tickets disponibles');
    }

    // Prepare Client Object
    const clientPayload = {
      full_name: clientData.full_name,
      cedula: clientData.document_id,
      phone: userPhone.value || clientData.phone,
      email: clientData.email,
      payment_method: clientData.payment_method,
      payment_ref: clientData.payment_ref,
      payment_source_email: clientData.payment_source_email,
      payment_issuing_bank: clientData.payment_issuing_bank,
      payment_source_number: clientData.payment_source_number,
      address_country: clientData.country,
      amount_bs: clientData.amount_bs,
      payment_exact_date: clientData.payment_exact_date,
    };

    let reserveResult;
    if (selectionMode.value === 'manual') {
      // Use Manual Selection Endpoint
      reserveResult = await api.reserveManualTickets({
        raffle_id: currentRaffle.value.id,
        ticket_numbers: selectedTicketNumbers.value,
        client: clientPayload,
      });
    } else {
      // Use Random Selection Endpoint
      reserveResult = await api.reserveTickets({
        raffle_id: currentRaffle.value.id,
        quantity: selectedQuantity.value,
        client: clientPayload,
      });
    }

    if (!reserveResult || reserveResult.error) {
      throw new Error(reserveResult?.error || 'Error desconocido en la reserva');
    }

    // SAVE ORDER DATA (Use server assigned tickets)
    lastOrderData.value = {
      orderId: reserveResult.order_id || reserveResult.sale_id || reserveResult.id,
      totalAmount: totalAmount.value,
      tickets: reserveResult.tickets || [], // Assigned by server
      client: clientPayload,
      date: new Date().toISOString(),
    };

    // Success Flow
    purchaseState.value = 'success';
    showCheckout.value = false;
    showSuccess.value = true;

    if (currentRaffle.value) {
      Analytics.purchase(
        lastOrderData.value.orderId,
        currentRaffle.value,
        ticketsToCheck,
        totalAmount.value,
      );
    }

    // Clear selection
    selectedQuantity.value = 0;
    selectedTicketNumbers.value = [];
    await loadTickets();
  } catch (e: any) {
    console.error(e);
    if (e.message?.includes('TICKET_ALREADY_SOLD') || e.toString().includes('TICKET_ALREADY_SOLD')) {
      feedback.value = {
        show: true,
        title: '¡Ticket No Disponible!',
        message: 'Uno de los tickets que intentas comprar acaba de ser vendido a otro usuario. Por favor selecciona otro.',
        type: 'error',
      };
      await loadTickets(); // Refresh grid
    } else {
      showToast(e.message || 'Error al procesar la compra', 'error');
    }
    purchaseState.value = 'idle';
  }
};
</script>

<template>
  <div
    class="min-h-screen bg-background text-white selection:bg-gold-500 selection:text-black font-sans"
  >
    <!-- Background Elements -->
    <div class="fixed top-0 left-0 w-full h-full pointer-events-none overflow-hidden z-0">
      <div
        class="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-gold-600/10 rounded-full blur-[120px]"
      ></div>
      <div
        class="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-purple-900/20 rounded-full blur-[100px]"
      ></div>
    </div>

    <!-- Navigation -->
    <NavBar @navigate="handleNavigate" />

    <ElLazoGanaderoLanding v-if="showLazoLanding" @buy="handleLazoBuy" />

    <main
      class="max-w-7xl mx-auto p-4 md:p-8 lg:p-12 relative z-10 min-h-[80vh]"
      v-if="currentRaffle && !showLazoLanding"
    >
      <!-- STEP 1: LANDING HERO (Updated) -->
      <div v-if="flowStep === 'landing'">
        <div class="grid lg:grid-cols-2 gap-12 items-center mb-20">
          <!-- Hero Text & CTA -->
          <div
            class="space-y-8 text-center lg:text-left animate-in slide-in-from-left-8 duration-700"
          >
            <!-- Logo Mobile/Desktop -->
            <img
              src="/images/logo.png"
              alt="Rifas La Favorita - Logo oficial"
              class="w-48 mx-auto lg:mx-0 mb-6 drop-shadow-[0_0_15px_rgba(255,215,0,0.5)]"
              width="192"
              height="192"
              loading="eager"
              fetchpriority="high"
            />

            <h1 class="text-5xl lg:text-7xl font-black leading-tight text-white">
              Atrapa tu<br />
              Suerte
            </h1>

            <p class="text-lg text-gray-400 max-w-lg mx-auto lg:mx-0">
              Participa en el sorteo más exclusivo de Venezuela. ¡Premios increíbles para el mayor comprador!
            </p>

            <!-- Countdown / Info Banner -->
            <div
              class="inline-flex gap-6 p-4 rounded-2xl bg-surface/50 border border-white/10 backdrop-blur-md items-center justify-center lg:justify-start"
            >
              <div class="text-left">
                <p class="text-xs text-gray-400 uppercase font-bold">🏆 Próximo Sorteo</p>
                <p class="font-mono text-xl text-gold-400 font-bold">{{ currentRaffle?.name || 'Gran Sorteo' }}</p>
              </div>
              
              <div class="w-px h-8 bg-white/10"></div>
              
              <div class="text-left">
                <p class="text-xs text-gray-400 uppercase font-bold">🎫 Vendidos</p>
                <p class="font-mono text-xl text-white font-bold">{{ takenTickets.length }}</p>
              </div>

              <div class="w-px h-8 bg-white/10"></div>

              <div class="text-left">
                <p class="text-xs text-gray-400 uppercase font-bold">🎟️ Disponibles</p>
                <div class="flex items-center gap-2">
                  <p class="font-mono text-xl text-green-400 font-bold">
                    {{ (currentRaffle.total_tickets || 1000) - takenTickets.length }}
                  </p>
                </div>
              </div>
            </div>

            <!-- CTA Buttons -->
            <div class="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <button
                @click="startPurchase"
                aria-label="Comprar tickets para participar en el sorteo"
                class="px-8 py-4 bg-gold-500 hover:bg-gold-400 text-black font-black text-lg rounded-full shadow-[0_0_40px_rgba(245,158,11,0.4)] transition-all hover:scale-105 hover:shadow-[0_0_60px_rgba(245,158,11,0.6)] flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-gold-500 focus:ring-offset-2 focus:ring-offset-black"
              >
                <span>🎫 COMPRAR TICKETS</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M5 12h14" />
                  <path d="m12 5 7 7-7 7" />
                </svg>
              </button>

              <!-- WhatsApp Report CTA -->
              <a
                href="https://wa.me/584123207976?text=*Reporte%20de%20Pago%20Rifas%20La%20Favorita*%0ANombre%20de%20cliente:%0ACI:%0AMetodo%20de%20Pago:%0ARefencia:%0ASoporte%20Grafico%0A%0A_Todo%20pago%20sera%20validado%20en%20un%20lapso%20no%20mayor%20de%2024%20horas%20y%20sera%20notificado,%20ya%20que%20su%20ticket%20pasa%20por%20un%20proceso%20de%20validacion%20y%20autenticacion_&utm_source=website&utm_medium=cta&utm_campaign=payment_report"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Reportar pago por WhatsApp (abre en nueva pestaña)"
                @click="Analytics.trackContact('whatsapp', 'Reportar Pago Hero')"
                class="px-8 py-4 bg-green-600 hover:bg-green-500 text-white font-bold text-lg rounded-full shadow-[0_0_20px_rgba(22,163,74,0.4)] transition-all hover:scale-105 flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:ring-offset-black"
              >
                <span aria-hidden="true">📱</span>
                <span>REPORTAR PAGO</span>
              </a>

              <!-- Instagram CTA -->
              <a
                href="https://instagram.com/rifaslafavorita?utm_source=website&utm_medium=social_icon&utm_campaign=hero"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Seguir a Rifas La Favorita en Instagram (abre en nueva pestaña)"
                @click="Analytics.trackInternalLink('Instagram Hero', 'instagram_profile')"
                class="p-4 bg-gradient-to-tr from-yellow-500 via-red-500 to-purple-600 rounded-full text-white hover:scale-110 transition-transform shadow-lg shadow-pink-500/30 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 focus:ring-offset-black"
              >
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path
                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.069-4.85.069-3.204 0-3.584-.012-4.849-.069-3.225-.149-4.771-1.664-4.919-4.919-.058-1.265-.069-1.644-.069-4.849 0-3.204.012-3.584.069-4.849.149-3.225 1.664-4.771 4.919-4.919 1.266-.058 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.36-.2 6.78-2.618 6.98-6.98.058-1.28.072-1.689.072-4.948 0-3.259-.014-3.667-.072-4.947-.2-4.361-2.618-6.78-6.98-6.98-1.28-.059-1.688-.073-4.948-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"
                  />
                </svg>
              </a>
            </div>

            <!-- Top Buyer Promo -->
            <div
              class="mt-8 p-4 bg-linear-to-r from-red-900/40 to-black border border-red-500/30 rounded-xl flex items-center gap-4 max-w-md mx-auto lg:mx-0"
            >
              <div
                class="w-12 h-12 rounded-full bg-red-600 flex items-center justify-center text-2xl shadow-lg shadow-red-500/20"
              >
                🐂
              </div>
              <div class="text-left">
                <h4 class="font-bold text-white text-sm">¡150 MAUTES EN PIE! 🚀</h4>
                <p class="text-xs text-gray-300">
                  Premio especial al participante con más tickets comprados.
                </p>
              </div>
            </div>
          </div>

          <!-- Hero Visual (Image) -->
          <div
            class="relative animate-in slide-in-from-right-8 duration-700 delay-100 flex justify-center"
          >
            <!-- Glow Effect -->
            <div class="absolute inset-0 bg-gold-500/20 blur-[100px] rounded-full scale-75"></div>
            <!-- Main Promo Image -->
            <img
              src="/images/hero-promo.jpeg"
              alt="Sorteo exclusivo de Rifas La Favorita - Imagen promocional del premio principal"
              class="relative w-full max-w-lg rounded-3xl shadow-2xl border-4 border-gold-500/30 rotate-1 hover:rotate-0 transition-all duration-500"
              width="800"
              height="800"
              loading="eager"
              fetchpriority="high"
            />

            <!-- Floating Elements -->
            <div
              class="absolute -bottom-6 -right-6 bg-surface border border-gold-500/30 p-4 rounded-2xl shadow-xl backdrop-blur-xl animate-bounce duration-[3000ms]"
            >
              <p class="text-xs text-gray-400 font-bold uppercase mb-1">💎 Valor del Ticket</p>
              <p class="text-3xl font-black text-gold-400">20.00</p>
            </div>


          </div>
        </div>

        <!-- Showcase Removed (Sorteo Anterior) -->
        <!-- <PrizeShowcase :price="currentRaffle.ticket_price" @buy="startPurchase" /> -->
      </div>

      <!-- STEP 2: REGISTER PHONE -->
      <div
        v-else-if="flowStep === 'register'"
        class="max-w-md mx-auto py-12 text-center animate-in fade-in zoom-in-95 duration-500"
      >
        <h2 class="text-3xl font-bold mb-4">Comencemos</h2>
        <p class="text-gray-400 mb-8">Ingresa tu número telefónico para gestionar tus tickets.</p>

        <div class="space-y-4">
          <input
            v-model="userPhone"
            type="tel"
            placeholder="0414-123-4567"
            class="w-full bg-surface border border-white/20 rounded-xl px-6 py-4 text-lg text-center focus:border-gold-500 focus:outline-none transition-colors"
          />

          <button
            @click="handleRegister"
            :disabled="!userPhone || userPhone.length < 10"
            class="w-full py-4 bg-gold-500 hover:bg-gold-400 text-black font-bold text-lg rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Continuar
          </button>
        </div>
      </div>

      <!-- STEP 3: SELECT TICKETS (Random or Manual) -->
      <div
        v-else-if="flowStep === 'select_tickets'"
        class="max-w-6xl mx-auto py-8 animate-in fade-in slide-in-from-right-8 duration-500"
      >
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold mb-2">Selecciona tus Tickets</h2>
          <p class="text-gray-400">Elige cómo quieres participar</p>
        </div>

        <!-- Mode Selection (only if manual selection is enabled) -->
        <div v-if="currentRaffle?.allowManualSelection" class="flex justify-center gap-4 mb-8">
          <button
            @click="
              selectionMode = 'random';
              selectedTicketNumbers = [];
            "
            :class="
              selectionMode === 'random'
                ? 'bg-gold-500 text-black'
                : 'bg-surface text-white hover:bg-white/10'
            "
            class="px-6 py-3 rounded-xl font-bold transition-all"
          >
            🎲 Aleatorio
          </button>
          <button
            @click="
              selectionMode = 'manual';
              selectedTicketNumbers = [];
            "
            :class="
              selectionMode === 'manual'
                ? 'bg-gold-500 text-black'
                : 'bg-surface text-white hover:bg-white/10'
            "
            class="px-6 py-3 rounded-xl font-bold transition-all"
          >
            🔢 Seleccionar Números
          </button>
        </div>

        <!-- Random Mode -->
        <div v-if="selectionMode === 'random'">
          <!-- Quantity Input & Shortcuts -->
          <div class="mb-12">
            <div class="flex justify-center mb-8">
              <div
                class="bg-surface border border-white/20 p-2 rounded-2xl flex items-center gap-4 max-w-xs w-full shadow-lg"
              >
                <button
                  @click="selectedQuantity = Math.max(1, selectedQuantity - 1)"
                  class="w-12 h-12 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-2xl text-white font-bold transition"
                >
                  -
                </button>
                <div class="flex-1 text-center">
                  <div class="text-[10px] text-gray-400 uppercase font-bold text-center mb-1">
                    CANTIDAD
                  </div>
                  <input
                    v-model.number="selectedQuantity"
                    @change="Analytics.addToCart(currentRaffle, selectedQuantity, totalAmount)"
                    type="number"
                    min="1"
                    max="1000"
                    class="w-full bg-transparent text-center text-4xl font-black text-white focus:outline-none appearance-none"
                  />
                </div>
                <button
                  @click="
                    selectedQuantity++;
                    Analytics.addToCart(currentRaffle, selectedQuantity, totalAmount);
                  "
                  class="w-12 h-12 rounded-xl bg-gold-500 hover:bg-gold-400 flex items-center justify-center text-2xl text-black font-bold transition shadow-lg shadow-gold-500/20"
                >
                  +
                </button>
              </div>
            </div>

            <div class="grid md:grid-cols-3 gap-6">
              <!-- Option 1: Single Ticket -->
              <div
                @click="
                  selectedQuantity = 1;
                  Analytics.addToCart(currentRaffle, 1, currentRaffle.ticketPrice || 20);
                "
                class="relative cursor-pointer group bg-surface border-2 rounded-2xl p-6 transition-all hover:scale-105"
                :class="
                  selectedQuantity === 1
                    ? 'border-gold-500 bg-gold-500/10'
                    : 'border-white/10 hover:border-gold-500/50'
                "
              >
                <div class="text-center">
                  <div class="text-3xl font-black text-white mb-1">1</div>
                  <div class="text-xs uppercase tracking-widest text-gray-400 font-bold">
                    Ticket
                  </div>
                  <div class="text-xl font-bold text-gold-400 mt-2">
                    🥬 {{ (currentRaffle.ticketPrice || 20).toFixed(2) }}
                  </div>
                </div>
              </div>

              <!-- Bundles -->
              <div
                v-for="bundle in currentRaffle?.ticketBundles || []"
                :key="bundle.qty"
                @click="
                  selectedQuantity = bundle.qty;
                  Analytics.addToCart(currentRaffle, bundle.qty, bundle.price);
                "
                class="relative cursor-pointer group bg-gradient-to-b from-[#1a1500] to-black border-2 border-gold-500 rounded-2xl p-6 transition-all transform hover:scale-105 shadow-xl md:col-span-2"
                :class="selectedQuantity === bundle.qty ? 'ring-4 ring-gold-500/30' : ''"
              >
                <div
                  class="absolute -top-3 left-1/2 -translate-x-1/2 bg-red-600 text-white font-bold px-3 py-1 rounded-full text-xs shadow-lg whitespace-nowrap"
                >
                  🔥 MEJOR OPCIÓN
                </div>
                <div class="text-center pt-2">
                  <div class="text-4xl font-black text-white mb-1">{{ bundle.qty }}</div>
                  <div class="text-xs uppercase tracking-widest text-gold-500 font-bold">
                    Tickets
                  </div>
                  <div class="flex items-center justify-center gap-2 mt-2">
                    <span class="text-gray-500 line-through text-sm"
                      >${{ (bundle.qty * (currentRaffle.ticketPrice || 20)).toFixed(0) }}</span
                    >
                    <span class="text-3xl font-bold text-green-400"
                      >🥬 {{ bundle.price.toFixed(2) }}</span
                    >
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Manual Mode -->
        <div v-else-if="selectionMode === 'manual'">
          <div class="bg-surface border border-white/10 rounded-2xl p-6 mb-6">
            <div class="flex justify-between items-center mb-4">
              <span class="text-gray-400">Tickets seleccionados</span>
              <span class="text-2xl font-bold text-gold-400">{{
                selectedTicketNumbers.length
              }}</span>
            </div>
            <!-- Search by number -->
            <div class="mb-4">
              <input
                type="number"
                placeholder="Buscar ticket (0-9999)"
                v-model.number="manualSearchNumber"
                @keypress.enter="jumpToTicket(manualSearchNumber)"
                class="w-full bg-black/50 border border-white/20 rounded-lg p-3 text-white font-mono text-center focus:border-gold-500 outline-none"
              />
              <p class="text-xs text-gray-500 mt-1">
                Presiona Enter para ir al ticket o selecciona del grid
              </p>
            </div>
            <!-- Ticket Grid -->
            <div
              class="grid grid-cols-5 md:grid-cols-10 lg:grid-cols-10 gap-2 max-h-[500px] overflow-y-auto p-2"
            >
              <button
                v-for="i in (currentRaffle?.total_tickets || 10000)"
                :key="i"
                :data-ticket-number="i - 1"
                @click="toggleTicket(i - 1)"
                :class="[
                  'aspect-square text-xs font-bold rounded transition-all flex items-center justify-center',
                  soldTickets.includes(i - 1)
                    ? 'bg-red-900/50 text-red-500 border border-red-900 cursor-not-allowed'
                    : reservedTickets.includes(i - 1)
                      ? 'bg-orange-900/50 text-orange-400 border border-orange-900 cursor-not-allowed'
                      : takenTickets.includes(i - 1)
                        ? 'bg-red-900/50 text-red-400 cursor-not-allowed'
                        : selectedTicketNumbers.includes(i - 1)
                          ? 'bg-gold-500 text-black'
                          : 'bg-white/10 text-white hover:bg-white/20',
                ]"
                :title="soldTickets.includes(i - 1) ? 'Vendido' : reservedTickets.includes(i - 1) ? 'Apartado' : ''"
                :disabled="takenTickets.includes(i - 1)"
              >
                {{ (i - 1).toString().padStart(4, '0') }}
              </button>
            </div>
            <p class="text-xs text-gray-500 mt-2 text-center">
              Mostrando {{ currentRaffle?.total_tickets || 10000 }} tickets.
            </p>
          </div>
        </div>

        <!-- Summary & Action -->
        <div class="max-w-md mx-auto bg-surface border border-white/10 rounded-2xl p-6">
          <div class="flex justify-between items-center mb-4">
            <span class="text-gray-400">Total a Pagar</span>
            <div class="text-right">
              <div class="text-3xl font-bold text-white">
                🥬 {{ totalAmount.toLocaleString('es-VE', { minimumFractionDigits: 2 }) }}
              </div>
              <div v-if="selectedTicketNumbers.length > 0" class="text-xs text-gray-400">
                {{ selectedTicketNumbers.length }} tickets seleccionados
              </div>
              <div v-else-if="selectedQuantity === 3" class="text-xs text-green-400">
                Promo aplicada
              </div>
            </div>
          </div>
          <button
            @click="openCheckout"
            :disabled="
              (selectionMode === 'random' && selectedQuantity === 0) ||
              (selectionMode === 'manual' && selectedTicketNumbers.length === 0)
            "
            class="w-full py-4 bg-green-500 hover:bg-green-400 text-white font-bold text-lg rounded-xl shadow-lg shadow-green-500/20 transition-all disabled:opacity-50 disabled:grayscale"
          >
            Confirmar Compra
          </button>
        </div>
      </div>
        <!-- Static Content Pages (Terms, Privacy, Contact, etc.) -->
        <div v-else-if="['terms', 'privacy', 'rules', 'help', 'contact', 'how_it_works'].includes(flowStep)">
             <StaticContent
              :page="flowStep as any"
              @close="handleNavigate('landing')"
            />
        </div>

    </main>

    <div v-else-if="!loading && closedRaffles.length > 0" class="max-w-7xl mx-auto p-8 mt-12">
      <div class="text-center mb-12">
        <div class="inline-block mb-4">
          <span class="text-6xl">🏆</span>
        </div>
        <h2 class="text-4xl font-black text-white mb-4">Sorteos Recientes</h2>
        <p class="text-gray-400">
          Actualmente no hay sorteos activos, pero puedes ver nuestro historial de sorteos
          anteriores y sus respectivos ganadores.
        </p>
      </div>

      <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
        <div
          v-for="raffle in closedRaffles"
          :key="raffle.id"
          class="bg-surface border border-white/10 rounded-2xl p-6 hover:border-gold-500/50 transition-all"
        >
          <h3 class="text-xl font-bold text-gold-400 mb-2">
            {{ raffle.title }}
          </h3>

          <div class="space-y-2 text-sm mb-4">
            <div class="flex justify-between text-gray-400">
              <span>Fecha:</span>
              <span class="text-white">{{
                new Date(raffle.end_at).toLocaleDateString('es-VE')
              }}</span>
            </div>
            <div class="flex justify-between text-gray-400">
              <span>Tickets vendidos:</span>
              <span class="text-white font-bold"
                >{{ raffle.sold_tickets }}/{{ raffle.total_tickets }}</span
              >
            </div>
            <div class="flex justify-between text-gray-400">
              <span>Porcentaje:</span>
              <span class="text-white font-bold">
                {{ ((raffle.sold_tickets / raffle.total_tickets) * 100).toFixed(1) }}%
              </span>
            </div>
          </div>

          <div
            v-if="raffle.winner_info"
            class="p-4 bg-green-500/10 rounded-xl border border-green-500/20"
          >
            <p class="text-green-400 text-sm font-bold mb-1">🏆 Ganador:</p>
            <p class="text-white font-semibold">{{ raffle.winner_info.name }}</p>
            <p class="text-gold-400 font-mono text-lg mt-1">
              Ticket #{{ String(raffle.winner_info.ticket_number).padStart(4, '0') }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="text-center p-8 bg-gradient-to-r from-gold-500/10 to-amber-600/10 rounded-2xl border border-gold-500/30"
      >
        <h3 class="text-2xl font-bold text-white mb-2">📱 ¿Quieres ser el próximo ganador?</h3>
        <p class="text-gray-400 mb-4">
          Síguenos en redes sociales para estar al tanto de nuestros próximos sorteos
        </p>
        <div class="flex justify-center gap-4">
          <a
            href="#"
            class="bg-pink-500 hover:bg-pink-600 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Instagram
          </a>
          <a
            href="#"
            class="bg-blue-500 hover:bg-blue-600 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Facebook
          </a>
          <a
            href="#"
            class="bg-green-500 hover:bg-green-600 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            WhatsApp
          </a>
        </div>
      </div>
    </div>

    <div v-else-if="!showLazoLanding" class="flex items-center justify-center min-h-screen p-8">
      <div class="text-center max-w-xl">
        <div class="text-8xl mb-6">🎫</div>
        <h2 class="text-3xl font-bold text-white mb-4">¡Sorteo Activo!</h2>
        <p class="text-gray-400 text-lg mb-8">
          Participa en el Gran Sorteo de 150 Mautes. Selecciona tus números y compra tus tickets
          ahora.
        </p>
        <button
          @click="handleRegister"
          class="bg-green-500 hover:bg-green-600 text-white font-bold px-8 py-4 rounded-xl transition-colors text-lg"
        >
          Participar Ahora
        </button>
      </div>
    </div>

    <!-- Modal -->
    <CheckoutModal
      :show="showCheckout"
      :total-amount="totalAmount"
      :exchange-rate="exchangeRate"
      :exchange-date="exchangeDate"
      :ticket-count="selectedQuantity"
      :loading="purchaseState === 'reserving'"
      :email="userEmail"
      @close="showCheckout = false"
      @confirm="handlePurchase"
    />

    <!-- Success Modal -->
    <SuccessModal :show="showSuccess" :order-data="lastOrderData" @close="handleSuccessClose" />

    <!-- My Tickets Modal -->
    <MyTicketsModal :show="showMyTickets" @close="showMyTickets = false" />

    <!-- Ticket Verifier -->
    <TicketVerifier
      v-if="flowStep === 'verify' && verifyParams"
      :id="verifyParams.id"
      :sig="verifyParams.sig"
    />

    <FeedbackModal 
      :show="feedback.show" 
      :type="feedback.type" 
      :title="feedback.title" 
      :message="feedback.message" 
      @close="feedback.show = false" 
    />

    <!-- Previous Winners Section -->
    <div
      v-if="flowStep === 'winners'"
      class="max-w-7xl mx-auto p-8 animate-in fade-in slide-in-from-bottom-8"
    >
      <h2 class="text-4xl font-black text-center mb-12 text-gold-500">🏆 Ganadores Anteriores</h2>
      <div class="grid md:grid-cols-3 gap-8">
        <!-- Mock Winner 1 -->
        <div
          class="bg-surface border border-gold-500/30 rounded-2xl p-6 text-center shadow-2xl relative overflow-hidden group hover:scale-105 transition-all"
        >
          <div
            class="absolute inset-0 bg-gold-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
          ></div>
          <div
            class="w-24 h-24 mx-auto rounded-full bg-linear-to-br from-gold-400 to-yellow-600 mb-4 flex items-center justify-center text-4xl shadow-lg border-4 border-black"
          >
            👤
          </div>
          <h3 class="text-xl font-bold text-white mb-1">María González</h3>
          <p class="text-gold-400 font-mono font-bold mb-4">Ticket #1234</p>
          <div class="bg-black/50 rounded-xl p-3 inline-block">
            <p class="text-xs text-gray-400 uppercase font-bold">Premio</p>
            <p class="text-lg font-bold text-green-400">💵 $5,000</p>
          </div>
          <p class="text-xs text-gray-500 mt-4">Sorteo Noviembre 2024</p>
        </div>
        <!-- Mock Winner 2 -->
        <div
          class="bg-surface border border-gold-500/30 rounded-2xl p-6 text-center shadow-2xl relative overflow-hidden group hover:scale-105 transition-all"
        >
          <div
            class="absolute inset-0 bg-gold-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
          ></div>
          <div
            class="w-24 h-24 mx-auto rounded-full bg-linear-to-br from-gold-400 to-yellow-600 mb-4 flex items-center justify-center text-4xl shadow-lg border-4 border-black"
          >
            👮‍♂️
          </div>
          <h3 class="text-xl font-bold text-white mb-1">Alexander Martinez</h3>
          <p class="text-gold-400 font-mono font-bold mb-4">Ticket #262</p>
          <div class="bg-black/50 rounded-xl p-3 inline-block">
            <p class="text-xs text-gray-400 uppercase font-bold">Premio</p>
            <p class="text-lg font-bold text-green-400">10 k</p>
          </div>
          <p class="text-xs text-gray-500 mt-4">Sorteo enero 2026</p>
        </div>
        <!-- Mock Winner 3 -->
        <div
          class="bg-surface border border-gold-500/30 rounded-2xl p-6 text-center shadow-2xl relative overflow-hidden group hover:scale-105 transition-all"
        >
          <div
            class="absolute inset-0 bg-gold-500/10 opacity-0 group-hover:opacity-100 transition-opacity"
          ></div>
          <div
            class="w-24 h-24 mx-auto rounded-full bg-linear-to-br from-gold-400 to-yellow-600 mb-4 flex items-center justify-center text-4xl shadow-lg border-4 border-black"
          >
            👩‍⚕️
          </div>
          <h3 class="text-xl font-bold text-white mb-1">Jhon Pereira</h3>
          <p class="text-gold-400 font-mono font-bold mb-4">Ticket #7034</p>
          <div class="bg-black/50 rounded-xl p-3 inline-block">
            <p class="text-xs text-gray-400 uppercase font-bold">Premio</p>
            <p class="text-lg font-bold text-green-400">Finca El Paraiso</p>
          </div>
          <p class="text-xs text-gray-500 mt-4">Sorteo Diciembre 2025</p>
        </div>
      </div>
      <div class="text-center mt-12">
        <button @click="handleNavigate('landing')" class="text-gray-400 hover:text-white underline">
          Volver al Inicio
        </button>
      </div>
    </div>

    <!-- How It Works is now handled by StaticContent -->



    <!-- Contact and How It Works are now handled by StaticContent -->

    <Footer @navigate="handleNavigate" />

    <!-- Offline Banner -->
    <div
      v-if="isOffline"
      class="fixed bottom-0 left-0 w-full bg-red-600 text-white text-center py-2 z-50 animate-in slide-in-from-bottom font-bold text-sm"
    >
      Sin conexión a internet. Verificando...
    </div>

    <!-- Global Toast -->
    <Toast
      :show="toast.show"
      :message="toast.message"
      :type="toast.type"
      @close="toast.show = false"
    />
  </div>
</template>

<style>
/* Utilities specific to this component */
.bg-surface {
  background-color: #121212;
}
.text-gold-400 {
  color: #fbbf24;
}
.text-gold-500 {
  color: #f59e0b;
}
.bg-gold-400 {
  background-color: #fbbf24;
}
.bg-gold-500 {
  background-color: #f59e0b;
}
.bg-gold-600 {
  background-color: #d97706;
}
</style>
